import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@v2/components/ui/button';

export interface UploadDropzoneProps {
  value?: { id: string; url: string }[];
  onChange?: (files: File[]) => void;
  onUpload?: (
    file: File,
    setProgress: (percent: number) => void
  ) => Promise<void>;
  onUploadComplete?: (successful: File[], failed: File[]) => void;
  accept?: string | string[];
  allowMultiple?: boolean;
  className?: string;
  type?: 'images' | 'vector-images' | 'all-images' | 'videos' | 'documents';
  autoUpload?: boolean;
}

export interface UploadStatus {
  file?: File;
  id?: string;
  url?: string;
  progress: number;
  status: 'idle' | 'uploading' | 'done' | 'error';
}

const typeToAcceptMap: Record<
  NonNullable<UploadDropzoneProps['type']>,
  string[]
> = {
  images: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  'vector-images': ['image/svg+xml'],
  'all-images': [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/svg+xml'
  ],
  videos: [
    'video/mp4',
    'video/webm',
    'video/ogg',
    'video/quicktime',
    'video/x-msvideo'
  ],
  documents: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ]
};

export const UploadDropzone: React.FC<UploadDropzoneProps> = ({
  value,
  onChange,
  onUpload,
  onUploadComplete,
  accept,
  allowMultiple = true,
  className = '',
  type,
  autoUpload = false
}) => {
  const [uploads, setUploads] = useState<UploadStatus[]>([]);

  useEffect(() => {
    if (value && value.length > 0) {
      setUploads(
        value.map((v) => ({
          id: v.id,
          url: v.url,
          progress: 100,
          status: 'done'
        }))
      );
    } else {
      setUploads([]);
    }
  }, [value]);

  const resolvedAccept = accept ?? (type ? typeToAcceptMap[type] : '*');
  const isUploading = uploads.some((u) => u.status === 'uploading');

  const updateProgress = (file: File, percent: number) => {
    setUploads((prev) =>
      prev.map((u) =>
        u.file === file
          ? { ...u, progress: Math.max(0, Math.min(percent, 100)) }
          : u
      )
    );
  };

  const updateStatus = (file: File, status: UploadStatus['status']) => {
    setUploads((prev) =>
      prev.map((u) =>
        u.file === file
          ? {
              ...u,
              status,
              progress: status === 'done' && u.progress < 100 ? 100 : u.progress
            }
          : u
      )
    );
  };

  const startUpload = async (uploadItems: UploadStatus[]) => {
    if (!onUpload) return;

    const successful: File[] = [];
    const failed: File[] = [];

    for (const item of uploadItems) {
      const file = item.file;
      if (!file) continue;

      updateProgress(file, 0);
      updateStatus(file, 'uploading');

      try {
        await onUpload(file, (progress) => updateProgress(file, progress));
        updateStatus(file, 'done');
        successful.push(file);
      } catch {
        updateStatus(file, 'error');
        failed.push(file);
      }
    }

    if (onUploadComplete) {
      onUploadComplete(successful, failed);
    }
  };

  const handleDrop = (files: File[]) => {
    if (isUploading) return;

    const initial: UploadStatus[] = files.map((file) => ({
      file,
      url: file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined,
      progress: 0,
      status: autoUpload ? 'uploading' : 'idle'
    }));

    const newUploads = allowMultiple ? [...uploads, ...initial] : initial;

    setUploads(newUploads);
    onChange?.(newUploads.map((u) => u.file!).filter(Boolean));

    if (autoUpload) {
      void startUpload(initial);
    }
  };

  const handleRemove = (target: UploadStatus) => {
    const updated = uploads.filter((u) =>
      u.file ? u.file !== target.file : u.id !== target.id
    );
    setUploads(updated);
    onChange?.(updated.map((u) => u.file!).filter(Boolean));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDrop,
    accept: resolvedAccept,
    multiple: allowMultiple,
    disabled: isUploading
  });

  const renderFileList = () => (
    <div className="space-y-2 mt-4">
      {uploads.map((u, index) => (
        <div key={u.id || u.file?.name || index}>
          <div className="flex justify-between items-center mb-1">
            <p className="text-sm text-gray-700">
              {u.file?.name || u.url?.split('/').pop() || 'Image'}
            </p>
          </div>
          <div className="w-full bg-gray-200 rounded h-2 overflow-hidden relative">
            {u.progress === 0 && u.status === 'uploading' ? (
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-400 to-blue-600 animate-pulse" />
            ) : (
              <div
                className={`h-2 rounded transition-all duration-200 ${
                  u.status === 'done'
                    ? 'bg-green-500'
                    : u.status === 'error'
                      ? 'bg-red-500'
                      : 'bg-blue-500'
                }`}
                style={{ width: `${u.progress}%` }}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderSingleImagePreview = () => {
    const u = uploads[0];
    const imageUrl = u?.url;
    if (!imageUrl) return null;

    return (
      <div className="mt-4 relative">
        <img
          src={imageUrl}
          alt="preview"
          className="max-w-full h-auto rounded shadow"
        />
      </div>
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-xl p-6 text-center transition-all relative
          ${isUploading ? 'pointer-events-none opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}
          ${isUploading ? 'bg-gray-100 border-gray-300' : 'bg-white border-gray-300'}
        `}
      >
        <input {...getInputProps()} />
        <p className="text-gray-600">
          Drag & drop files here, or{' '}
          <span className="text-blue-600 underline">click to browse</span>
        </p>

        {!allowMultiple && uploads.length === 1
          ? renderSingleImagePreview()
          : uploads.length > 0 && renderFileList()}
      </div>

      <div className="flex gap-2 items-center">
        {!autoUpload && uploads.some((u) => u.status === 'idle') && (
          <Button
            onClick={() =>
              startUpload(uploads.filter((u) => u.status === 'idle'))
            }
          >
            Start Upload
          </Button>
        )}
      </div>
    </div>
  );
};
